import { ChangeDetectionStrategy, Component, Input, OnInit, Renderer2, ViewEncapsulation } from '@angular/core';
import { AppTextService } from '@fi-shared/app-text';
import { LoadingService } from '@fi-shared/fi-loading/loading.service';
import { WidgetAdministrateService } from '@fi-app/widget/widget-administrate.service';
import { HtmlPreviewService } from '@fi-shared/fi-print-download/html-preview.service';
import { ObjectTypes } from '@fi-shared/types/object-type';
import { SaveChangesWarningService } from '@fi-shared/fi-shared-services/save-changes-warning.service';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { FiStateService } from '@fi-shared/fi-state-proxy/fi-state.proxy.service';
import { MobileTabsModel } from '@fi-app/utilities/mobile-tabs/mobile-tabs.model';
import { MobileTabModel } from '@fi-app/utilities/mobile-tabs/mobile-tab.model';
import { WidgetModel } from '@fi-app/widget/model/widget.model';
import { FiContentLanguageSharedService } from '@fi-shared/fi-content-language-shared/fi-content-language-shared.service';
import { FiStateNames } from '@fi-shared/fi-state-proxy/fi-state-names';
import { EditTabsModel } from '@fi-app/utilities/edit-tabs/edit-tabs.model';
import { DropdownListSettingsLanguageModel } from '@fi-shared/dropdown';
import { ITabItem } from '@fi-app/utilities/tabs/tabs';
import { WidgetAdministrateModel } from '@fi-app/widget/model/widget-administrate.model';
import {
    IContentLanguage,
    IDropdownSelectLanguageSettingsItem,
} from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { TextEditorHistoryService } from '@fi-shared/fi-text-editor/fi-text-editor-helper-services/text-editor-history.service';
import { FiUserDataService } from '@fi-shared/fi-shared-services/fi-user-data.service';
import { FiPrintDownloadService } from '@fi-shared/fi-print-download/fi-print-download.service';

enum StartPageTabs {
    Widgets,
    Settings,
}

@Component({
    selector: 'fi-appearance-start-page-edit',
    templateUrl: './edit.component.html',
    styleUrls: ['./edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-appearance-start-page-edit' },
    standalone: false,
})
export class FiAppearanceStartPageEditComponent implements OnInit {
    @Input() siteDesignId: number;
    @Input('widgets') availableWidgets: WidgetModel[];
    @Input('widgetAdministrate') widgetAdministrate: WidgetAdministrateModel;
    @Input('widgetAdministrateWidgets') widgetAdministrateWidgets;
    @Input('editStartPageModel') editStartPageModel;

    startPageTabs = StartPageTabs;
    optionsTabs: ITabItem[] = [];
    selectedOptionsTabIndex = 0;
    private defaultContentLanguageId: number;
    languageListData: DropdownListSettingsLanguageModel<IDropdownSelectLanguageSettingsItem>;
    device: number = 1280;
    deviceMaxWidth: number = 1280;
    mobileTabs: MobileTabsModel;
    editTabs: EditTabsModel;

    constructor(
        private appTextService: AppTextService,
        private loadingService: LoadingService,
        private widgetAdministrateService: WidgetAdministrateService,
        private htmlPreviewService: HtmlPreviewService,
        private fiPrintDownloadService: FiPrintDownloadService,
        private saveChangesWarningService: SaveChangesWarningService,
        private contentLanguagesService: ContentLanguagesService,
        private stateService: FiStateService,
        private contentLanguageSharedService: FiContentLanguageSharedService,
        private renderer: Renderer2,
        private textEditorHistoryService: TextEditorHistoryService,
        private fiUserDataService: FiUserDataService,
    ) {}

    ngOnInit(): void {
        this.textEditorHistoryService.resetChangesHistoryData();
        this.setInitialComponentData();
    }

    private setInitialComponentData() {
        this.editTabs = new EditTabsModel(
            this.appTextService.get('common.appearance'),
            this.appTextService.get('common.cancel'),
        );

        this.optionsTabs = [
            { Title: this.appTextService.get('common.widgets'), Value: StartPageTabs.Widgets },
            { Title: this.appTextService.get('settings'), Value: StartPageTabs.Settings },
        ];

        this.defaultContentLanguageId = this.fiUserDataService.userData.PreferredContentLanguage;
        this.languageListData = this.contentLanguagesService.GetDropdownListSettingsLanguage();
        this.saveChangesWarningService.start(() => this.widgetAdministrate);

        this.mobileTabs = new MobileTabsModel(this.appTextService.get('common.home'));
        this.mobileTabs.Tabs.push(new MobileTabModel(this.appTextService.get('common.home'), 'HomeTab'));
        this.mobileTabs.Tabs.push(new MobileTabModel(this.appTextService.get('common.more'), 'MoreTab'));
    }

    isViewSelected(view: number): boolean {
        return view === this.optionsTabs[this.selectedOptionsTabIndex].Value;
    }

    deselectControl() {
        this.editStartPageModel.Layout.SelectedControl.Settings.DateChanged = new Date();
        this.editStartPageModel.Layout.SelectedControl = null;
    }

    save() {
        // note: setTimeout is here for saving latest layout that might be changed after button click (e.g. removing extra classes)
        setTimeout(() => {
            const startPageLayout: Element = document.querySelector('.start-page-layout');
            this.renderer.addClass(startPageLayout, 'view-mode');
            this.loadingService.trackPromise(
                this.widgetAdministrateService.SaveWidgetAdministrate(this.widgetAdministrate).then((Id: number) => {
                    const previewHtml: string = this.htmlPreviewService.getPreviewHtml(
                        startPageLayout,
                        undefined,
                        (layout) => {
                            this.renderer.setStyle(layout, 'max-height', '800px');
                            this.renderer.setStyle(layout, 'overflow', 'hidden');
                        },
                    );
                    this.renderer.removeClass(startPageLayout, 'view-mode');

                    this.fiPrintDownloadService
                        .generatePreviewFileImage(ObjectTypes.FirstPage, Id, previewHtml)
                        .then(() => {
                            this.widgetAdministrateService.previewImageGenerated$.next();
                        });

                    this.saveChangesWarningService.stop();
                    this.textEditorHistoryService.resetChangesHistoryData();
                    this.stateService.go(FiStateNames.OtherSettings.SiteDesign.BrandEdit, { Id: this.siteDesignId });
                }),
            );
        }, 100);
        // setTimeout(() => {
        //     const layout = $('.start-page-layout');
        //     layout.addClass('view-mode');
        //
        //     this.loadingService.trackPromise(this.widgetAdministrateService.SaveWidgetAdministrate(this.WidgetAdministrate).then((Id) => {
        //         const previewHtml = this.htmlPreviewService.getPreviewHtml(layout.get(0), undefined, (layout) => {
        //             $(layout).css('max-height', '800px');
        //             $(layout).css('overflow', 'hidden');
        //         });
        //         layout.removeClass('view-mode');
        //
        //         this.fileUploadService.generatePreviewFileImage(ObjectTypes.FirstPage, Id, previewHtml).then(() => {
        //             this.widgetAdministrateService.previewImageGenerated$.next();
        //         });
        //
        //         this.saveChangesWarningService.stop();
        //         this.stateService.go(FiStateNames.Appearance.View);
        //     }))
        // }, 100);
    }

    getControlHeaderTitle(control): string {
        if (!control) return '';

        if (control.Settings.HeaderNames[this.editStartPageModel.selectedLanguage.id])
            return control.Settings.HeaderNames[this.editStartPageModel.selectedLanguage.id];

        if (control.Settings.HeaderNames[this.defaultContentLanguageId])
            return control.Settings.HeaderNames[this.defaultContentLanguageId];

        const headerNamesKeys: string[] = Object.keys(control.Settings.HeaderNames);
        if (!!headerNamesKeys.length) return control.Settings.HeaderNames[headerNamesKeys[0]];

        return this.appTextService.get(
            this.widgetAdministrateWidgets[this.editStartPageModel.Layout.SelectedControl.ccmInternalId].TextId,
        );
    }
}
